import * as React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import useI18n from '@i18n/useI18n'

interface Props {
  societeId: string
  stats?: ControleQualiteStatistiqueResponse
  statsOthers?: ControleQualiteStatistiqueResponse
  title: string
  onNavigation: (path: string, params: Record<string, any>) => void
}

const ControlePrestaCard = ({ societeId, stats, statsOthers, title, onNavigation }: Props) => {
  const i18n = useI18n()

  const onSansObservation = () => {
    onNavigation(`/controles/history/list`, { societeId, observation: false })
  }

  const onAvecObservation = () => {
    onNavigation(`/controles/history/list`, { societeId, observation: true })
  }

  const onNonConforme = () => {
    onNavigation(`/controles/history/list`, { societeId, conforme: false })
  }

  const avecObsPercent = stats
    ? stats.nombreControle === 0
      ? 0
      : Math.floor((stats.nombreControleAvecObservation * 100) / stats.nombreControle)
    : undefined
  const sansObsPercent = stats
    ? stats.nombreControle === 0
      ? 0
      : Math.floor((stats.nombreControleSansObservation * 100) / stats.nombreControle)
    : undefined
  const nonConformePercent = stats
    ? stats.nombreControle === 0
      ? 0
      : Math.floor((stats.nombreControleNonConforme * 100) / stats.nombreControle)
    : undefined

  return (
    <MainContainer>
      <TopRow>
        <TopText>
          <MainInfo numberOfLines={3} ellipsizeMode="tail">
            {title}
          </MainInfo>
          <Info>
            {stats
              ? i18n.t('components.controleManifCard.nombreControle', { count: stats.nombreControle })
              : i18n.t('errors.NC')}
          </Info>
        </TopText>

        <View>
          <Scores>
            <ScoreContainer>
              <ScoreInfo>{i18n.t('components.controlePrestaCard.meanScore')}</ScoreInfo>
              <Score>
                {stats && stats.tauxConformiteMoyen != null
                  ? `${Math.round(stats.tauxConformiteMoyen)}%`
                  : i18n.t('errors.NC')}
              </Score>
            </ScoreContainer>

            {!!statsOthers && (
              <OthersScoreContainer>
                <OthersScoreInfo>{i18n.t('components.controlePrestaCard.othersScore')}</OthersScoreInfo>
                <OtherScrore>
                  {statsOthers.tauxConformiteMoyen != null
                    ? `${Math.round(statsOthers.tauxConformiteMoyen)}%`
                    : i18n.t('errors.NC')}
                </OtherScrore>
              </OthersScoreContainer>
            )}
          </Scores>
        </View>
      </TopRow>
      <Stats>
        <Stat onPress={onSansObservation} activeOpacity={0.9}>
          <MainInfo>{stats ? stats.nombreControleSansObservation : i18n.t('errors.NC')}</MainInfo>
          <Info>{i18n.t('components.controleManifCard.nombreControleSansObservation')}</Info>
          <Separator />
          <Proportion>{sansObsPercent}%</Proportion>
        </Stat>

        <Stat onPress={onAvecObservation} activeOpacity={0.9}>
          <MainInfo>{stats ? stats.nombreControleAvecObservation : i18n.t('errors.NC')}</MainInfo>
          <Info>{i18n.t('components.controleManifCard.nombreControleAvecObservation')}</Info>
          <Separator />
          <Proportion>{avecObsPercent}%</Proportion>
        </Stat>

        <Stat isLast onPress={onNonConforme} activeOpacity={0.9}>
          <MainInfo>{stats ? stats.nombreControleNonConforme : i18n.t('errors.NC')}</MainInfo>
          <Info>{i18n.t('components.controleManifCard.nombreControleNonConforme')}</Info>
          <Separator />
          <Proportion>{nonConformePercent}%</Proportion>
        </Stat>
      </Stats>
    </MainContainer>
  )
}

export default ControlePrestaCard

const MainContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 360px;`}
  ${(props) => props.theme.constants.shadow};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  overflow: hidden;
  padding: 0 12px 16px;
  min-height: 230px;
`

const TopRow = styled(View)`
  flex-direction: row;
  flex: 1;
`

const TopText = styled(View)`
  padding-top: 16px;
  flex: 1;
`

const ScoreContainer = styled(View)`
  padding: 8px 4px;
  max-width: 65px;
  background-color: ${(props) => props.theme.colors.alertButtonLight};
  border: 1px solid ${(props) => props.theme.colors.waiting};
  border-top-width: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`

const ScoreInfo = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.alertButton};
  ${(props) => props.theme.breakpoint === 'phone' && `margin-bottom: 4px;`}
`

const Score = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.alertButton};
`

const OthersScoreContainer = styled(View)`
  padding: 8px 0 8px 4px;
  margin-left: 8px;
  max-width: 75px;
`

const OthersScoreInfo = styled(ScoreInfo)`
  color: ${(props) => props.theme.colors.secondaryText};
`

const OtherScrore = styled(Score)`
  color: ${(props) => props.theme.colors.secondaryText};
`

const Stats = styled(View)`
  flex-direction: row;
  margin-top: 8px;
`
const Stat = styled(TouchableOpacity)<{ isLast?: boolean }>`
  flex: 1;
  background-color: ${(props) => props.theme.colors.cardSecondary};
  padding: 8px;
  align-items: center;
  ${(props) => !props.isLast && `margin-right: 8px;`}
`

const MainInfo = styled(Text)`
  margin-bottom: 8px;
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Scores = styled(View)`
  flex-direction: row;
  margin-left: 8px;
`

const Separator = styled(View)`
  border: 0 solid ${(props) => props.theme.colors.background};
  border-top-width: 1px;
  margin-top: 12px;
  width: 100%;
`
const Proportion = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  padding: 12px 12px 4px;
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
`
